import trophyFilledIcon from "@tabler/icons/filled/trophy.svg";
import arrowDownIcon from "@tabler/icons/outline/arrow-down.svg";
import arrowUpIcon from "@tabler/icons/outline/arrow-up.svg";
import React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";

import { useGemProfile } from "@/api/gems";
import GemIcon from "@/components/premium/gem-icon";
import {
  Avatar,
  HStack,
  Icon,
  ProgressBar,
  Stack,
  Text,
} from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { shortNumberFormat } from "@/utils/numbers";

function formatGemRank(rank: string) {
  const rankNumber = parseInt(rank, 10);
  if (isNaN(rankNumber)) {
    return rank;
  }

  return <FormattedNumber value={rankNumber} />;
}

interface GemRankProps {
  isCondensed?: boolean;
}

const GemRank = ({ isCondensed }: GemRankProps) => {
  const { currentAccount } = useAuth();
  const { data: gemProfile } = useGemProfile();

  if (!currentAccount || !gemProfile) {
    return null;
  }

  const rankIsUp = gemProfile.change_24h > 0;

  const arrowIconSrc = rankIsUp ? arrowUpIcon : arrowDownIcon;

  const arrowIconClass = rankIsUp
    ? "text-primary-500 dark:text-primary-400"
    : "text-secondary-500 dark:text-secondary-500";

  const hasEarnedAllDailyGems = gemProfile.daily_percentage === 100;

  return (
    <Stack className="divide-y" data-testid="gem-rank">
      <HStack alignItems="bottom" justifyContent="between" className="pb-4">
        <HStack alignItems="center" space={4}>
          {!isCondensed && (
            <Avatar size={40} src={currentAccount.avatar_static} />
          )}

          <Stack>
            <Text size="sm" theme="muted" className="inline-flex">
              <FormattedMessage
                id="gem_rank.current_rank"
                defaultMessage="My current rank"
              />
            </Text>

            <HStack alignItems="center" space={1.5}>
              <Text
                weight="bold"
                size={isCondensed ? "xmd" : "2xl"}
                data-testid="gem-rank-number"
              >
                {formatGemRank(gemProfile.rank)}
              </Text>

              <HStack alignItems="center" space={0.5}>
                <Icon src={arrowIconSrc} className={arrowIconClass} size={14} />

                <Text size="xs" weight="medium" className={arrowIconClass}>
                  <FormattedMessage
                    id="gem_rank.time_frame"
                    defaultMessage="{value} last 24hrs"
                    values={{ value: gemProfile.change_24h }}
                  />
                </Text>
              </HStack>
            </HStack>
          </Stack>
        </HStack>

        <HStack alignItems="center" space={1}>
          <GemIcon className="size-6" />
          <Text weight="bold" size={isCondensed ? "xmd" : "2xl"}>
            {shortNumberFormat(currentAccount.source.gems_count)}
          </Text>
        </HStack>
      </HStack>

      <Stack className="pt-4" space={2}>
        <HStack alignItems="center" justifyContent="between">
          <Text theme="muted" className="text-[13px]!">
            <FormattedMessage
              id="gem_rank.daily_earned"
              defaultMessage="{percent} of possible daily Gems earned"
              values={{
                percent: (
                  <strong className="font-bold text-gray-900 dark:text-gray-100">
                    {gemProfile.daily_percentage}%
                  </strong>
                ),
              }}
            />
          </Text>

          {hasEarnedAllDailyGems && !isCondensed && (
            <Icon src={trophyFilledIcon} className="text-primary-500" />
          )}
        </HStack>

        <ProgressBar
          progress={gemProfile.daily_percentage / 100}
          size="sm"
          isGradient={!hasEarnedAllDailyGems}
        />
      </Stack>
    </Stack>
  );
};

export default GemRank;
